import api from '../services/api';

/**
 * MMS (Maintenance Management System) Model
 * Handles all API calls for LOCAL MMS data (artisans, assignments, activities, locations)
 * 
 * Note: For EDAMS job card data, use externalModel.js
 * Frontend components should call both APIs and combine the data in the UI
 */

// ============================================================================
// Dashboard (Local MMS Data Only)
// ============================================================================

/**
 * Get dashboard statistics (local MMS data only)
 * For EDAMS job card stats, call externalModel.getJobCardStats()
 */
export const getDashboardStats = async () => {
  try {
    const response = await api.get('/api/mms/dashboard');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw new Error('Failed to fetch dashboard statistics: ' + error.message);
  }
};

// ============================================================================
// Artisans
// ============================================================================

/**
 * Get all artisans with pagination and filtering
 */
export const getFieldWorkers = async (filters = {}) => {
  try {
    const response = await api.get('/api/mms/workers', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching artisans:', error);
    throw new Error('Failed to fetch artisans: ' + error.message);
  }
};

/**
 * Get all local MMS workers (simple list for dropdowns)
 * Returns all active workers without pagination, including active jobs count
 */
export const getLocalWorkers = async () => {
  try {
    const response = await api.get('/api/mms/workers/local');
    return response.data;
  } catch (error) {
    console.error('Error fetching local workers:', error);
    throw new Error('Failed to fetch local workers: ' + error.message);
  }
};

/**
 * Get a single artisan by ID
 */
export const getFieldWorker = async (workerId) => {
  try {
    const response = await api.get(`/api/mms/workers/${workerId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching artisan:', error);
    throw new Error('Failed to fetch artisan: ' + error.message);
  }
};

/**
 * Create a new artisan
 */
export const createFieldWorker = async (workerData) => {
  try {
    const response = await api.post('/api/mms/workers', workerData);
    return response.data;
  } catch (error) {
    console.error('Error creating artisan:', error);
    throw new Error('Failed to create artisan: ' + error.message);
  }
};

/**
 * Create a local artisan account from an EDAMS employee
 */
export const createWorkerFromEmployee = async (employeeNo, workerData) => {
  try {
    const response = await api.post(`/api/mms/workers/from-employee/${employeeNo}`, workerData);
    return response.data;
  } catch (error) {
    console.error('Error creating worker from employee:', error);
    throw new Error('Failed to create worker from employee: ' + error.message);
  }
};

/**
 * Update an existing artisan
 */
export const updateFieldWorker = async (workerId, workerData) => {
  try {
    const response = await api.put(`/api/mms/workers/${workerId}`, workerData);
    return response.data;
  } catch (error) {
    console.error('Error updating artisan:', error);
    throw new Error('Failed to update artisan: ' + error.message);
  }
};

/**
 * Delete an artisan
 */
export const deleteFieldWorker = async (workerId) => {
  try {
    const response = await api.delete(`/api/mms/workers/${workerId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting artisan:', error);
    throw new Error('Failed to delete artisan: ' + error.message);
  }
};



// ============================================================================
// Job Assignments
// ============================================================================

/**
 * Get all job assignments with pagination and filtering
 */
export const getAssignments = async (filters = {}) => {
  try {
    const response = await api.get('/api/mms/assignments', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching assignments:', error);
    throw new Error('Failed to fetch assignments: ' + error.message);
  }
};

/**
 * Get a single assignment by ID
 */
export const getAssignment = async (assignmentId) => {
  try {
    const response = await api.get(`/api/mms/assignments/${assignmentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching assignment:', error);
    throw new Error('Failed to fetch assignment: ' + error.message);
  }
};

/**
 * Create a new job assignment
 */
export const createAssignment = async (assignmentData) => {
  try {
    const response = await api.post('/api/mms/assignments', assignmentData);
    return response.data;
  } catch (error) {
    console.error('Error creating assignment:', error);
    throw new Error('Failed to create assignment: ' + error.message);
  }
};

/**
 * Update an assignment status
 */
export const updateAssignmentStatus = async (assignmentId, statusData) => {
  try {
    const response = await api.put(`/api/mms/assignments/${assignmentId}/status`, statusData);
    return response.data;
  } catch (error) {
    console.error('Error updating assignment status:', error);
    throw new Error('Failed to update assignment status: ' + error.message);
  }
};

/**
 * Complete an assignment
 */
export const completeAssignment = async (assignmentId, completionData) => {
  try {
    const response = await api.post(`/api/mms/assignments/${assignmentId}/complete`, completionData);
    return response.data;
  } catch (error) {
    console.error('Error completing assignment:', error);
    throw new Error('Failed to complete assignment: ' + error.message);
  }
};

/**
 * Get assignments for a specific worker
 */
export const getWorkerAssignments = async (workerId, filters = {}) => {
  try {
    const response = await api.get(`/api/mms/workers/${workerId}/assignments`, { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching worker assignments:', error);
    throw new Error('Failed to fetch worker assignments: ' + error.message);
  }
};

/**
 * Get all local job assignments
 */
export const getAllLocalAssignments = async (filters = {}) => {
  try {
    const response = await api.get('/api/mms/assignments/local', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching local assignments:', error);
    throw new Error('Failed to fetch local assignments: ' + error.message);
  }
};

// ============================================================================
// Activities & Logs
// ============================================================================

/**
 * Get activities for an assignment
 */
export const getAssignmentActivities = async (assignmentId) => {
  try {
    const response = await api.get(`/api/mms/assignments/${assignmentId}/activities`);
    return response.data;
  } catch (error) {
    console.error('Error fetching assignment activities:', error);
    throw new Error('Failed to fetch assignment activities: ' + error.message);
  }
};

/**
 * Get activities for a job card
 */
export const getJobActivities = async (jobCardNo) => {
  try {
    const response = await api.get(`/api/mms/activities/${jobCardNo}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching job activities:', error);
    throw new Error('Failed to fetch job activities: ' + error.message);
  }
};

/**
 * Add activity to an assignment
 */
export const addAssignmentActivity = async (assignmentId, activityData) => {
  try {
    const response = await api.post(`/api/mms/assignments/${assignmentId}/activities`, activityData);
    return response.data;
  } catch (error) {
    console.error('Error adding assignment activity:', error);
    throw new Error('Failed to add assignment activity: ' + error.message);
  }
};

/**
 * Add activity to a job card
 */
export const addJobActivity = async (activityData) => {
  try {
    const response = await api.post('/api/mms/activities', activityData);
    return response.data;
  } catch (error) {
    console.error('Error adding job activity:', error);
    throw new Error('Failed to add job activity: ' + error.message);
  }
};

/**
 * Get location logs for a worker
 */
export const getWorkerLocationLogs = async (workerId, filters = {}) => {
  try {
    const response = await api.get(`/api/mms/workers/${workerId}/locations`, { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching worker location logs:', error);
    throw new Error('Failed to fetch worker location logs: ' + error.message);
  }
};

// ============================================================================
// Mobile App Functions (for reference - typically called via mobile app)
// ============================================================================

/**
 * Mobile worker login
 */
export const mobileLogin = async (credentials) => {
  try {
    const response = await api.post('/api/mms/mobile/login', credentials);
    return response.data;
  } catch (error) {
    console.error('Error during mobile login:', error);
    throw new Error('Failed to login: ' + error.message);
  }
};

/**
 * Update worker location (typically called from mobile app)
 */
export const updateWorkerLocation = async (locationData) => {
  try {
    const response = await api.post('/api/mms/mobile/location', locationData);
    return response.data;
  } catch (error) {
    console.error('Error updating location:', error);
    throw new Error('Failed to update location: ' + error.message);
  }
};

/**
 * Get all workers with their current locations
 */
export const getWorkersLocations = async () => {
  try {
    const response = await api.get('/api/mms/locations');
    return response.data;
  } catch (error) {
    console.error('Error fetching workers locations:', error);
    throw new Error('Failed to fetch workers locations: ' + error.message);
  }
};

// ============================================================================
// Parts Management (Autocomplete)
// ============================================================================

/**
 * Get parts for autocomplete/search
 */
export const getParts = async (search = '', category = '', limit = 50) => {
  try {
    const params = { limit };
    if (search) params.search = search;
    if (category) params.category = category;
    
    const response = await api.get('/api/mms/parts', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching parts:', error);
    throw new Error('Failed to fetch parts: ' + error.message);
  }
};

/**
 * Get all part categories
 */
export const getPartCategories = async () => {
  try {
    const response = await api.get('/api/mms/parts/categories');
    return response.data;
  } catch (error) {
    console.error('Error fetching part categories:', error);
    throw new Error('Failed to fetch part categories: ' + error.message);
  }
};

/**
 * Create a new part
 */
export const createPart = async (partData) => {
  try {
    const response = await api.post('/api/mms/parts', partData);
    return response.data;
  } catch (error) {
    console.error('Error creating part:', error);
    throw new Error('Failed to create part: ' + error.message);
  }
};

/**
 * Update a part
 */
export const updatePart = async (partId, partData) => {
  try {
    const response = await api.put(`/api/mms/parts/${partId}`, partData);
    return response.data;
  } catch (error) {
    console.error('Error updating part:', error);
    throw new Error('Failed to update part: ' + error.message);
  }
};

/**
 * Delete a part
 */
export const deletePart = async (partId) => {
  try {
    const response = await api.delete(`/api/mms/parts/${partId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting part:', error);
    throw new Error('Failed to delete part: ' + error.message);
  }
};

// ============================================================================
// Child Jobs (Parent-Child Relationships)
// ============================================================================

/**
 * Create a child job for tool/equipment delivery
 */
export const createChildJob = async (parentAssignmentId, toolType, assignedTo = null) => {
  try {
    const response = await api.post(`/api/mms/assignments/${parentAssignmentId}/create-child-job`, {
      tool_type: toolType,
      assigned_to: assignedTo
    });
    return response.data;
  } catch (error) {
    console.error('Error creating child job:', error);
    throw new Error('Failed to create child job: ' + error.message);
  }
};

/**
 * Get child jobs for a parent assignment
 */
export const getChildJobs = async (parentAssignmentId) => {
  try {
    const response = await api.get(`/api/mms/assignments/${parentAssignmentId}/children`);
    return response.data;
  } catch (error) {
    console.error('Error fetching child jobs:', error);
    throw new Error('Failed to fetch child jobs: ' + error.message);
  }
};

/**
 * Get parent job for a child assignment
 */
export const getParentJob = async (childAssignmentId) => {
  try {
    const response = await api.get(`/api/mms/assignments/${childAssignmentId}/parent`);
    return response.data;
  } catch (error) {
    console.error('Error fetching parent job:', error);
    throw new Error('Failed to fetch parent job: ' + error.message);
  }
};

/**
 * Resolve a job relationship (mark dependency as complete)
 */
export const resolveRelationship = async (relationshipId, notes = '') => {
  try {
    const response = await api.put(`/api/mms/relationships/${relationshipId}/resolve`, { notes });
    return response.data;
  } catch (error) {
    console.error('Error resolving relationship:', error);
    throw new Error('Failed to resolve relationship: ' + error.message);
  }
};

/**
 * Get all blocked jobs
 */
export const getBlockedJobs = async () => {
  try {
    const response = await api.get('/api/mms/blocked-jobs');
    return response.data;
  } catch (error) {
    console.error('Error fetching blocked jobs:', error);
    throw new Error('Failed to fetch blocked jobs: ' + error.message);
  }
};

export default {
  // Dashboard (Local MMS Data)
  getDashboardStats,
  
  // Artisans
  getFieldWorkers,
  getFieldWorker,
  createFieldWorker,
  updateFieldWorker,
  deleteFieldWorker,
  
  // Assignments
  getAssignments,
  getAssignment,
  createAssignment,
  updateAssignmentStatus,
  completeAssignment,
  getWorkerAssignments,
  
  // Activities & Logs
  getAssignmentActivities,
  addAssignmentActivity,
  getJobActivities,
  addJobActivity,
  getWorkerLocationLogs,
  
  // Mobile
  mobileLogin,
  updateWorkerLocation,
  getWorkersLocations,
  
  // Parts
  getParts,
  getPartCategories,
  createPart,
  updatePart,
  deletePart,
  
  // Child Jobs
  createChildJob,
  getChildJobs,
  getParentJob,
  resolveRelationship,
  getBlockedJobs
};
